import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useStore } from "../lib/store";
import { readPreference } from "../features/preferences/contracts";

/**
 * Identidade exibida no topo, na saudação da Home e como autor das mensagens.
 *
 * A ordem é a do produto: o nome salvo em Configurações > Perfil é o que a
 * pessoa escolheu chamar-se, então vem primeiro; a sessão do Supabase é o
 * fallback; e sem os dois a interface mostra o rótulo neutro, sem inventar um
 * usuário. O e-mail continua vindo só da sessão — ele não se edita aqui.
 */
export interface ProfileIdentity {
  name: string;
  email?: string;
}

export const NEUTRAL_IDENTITY = "Minha conta";

export function useProfileIdentity(): ProfileIdentity {
  const store = useStore();
  const [session, setSession] = useState<ProfileIdentity | null>(null);
  const saved = readPreference(store.userPreferences ?? [], "profile").value.displayName?.trim();

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    void supabase.auth.getUser().then(({ data }) => {
      const user = data.user;
      if (!active || !user) return;
      setSession({
        name:
          (user.user_metadata.display_name as string | undefined) ||
          (user.user_metadata.full_name as string | undefined) ||
          user.email?.split("@")[0] ||
          NEUTRAL_IDENTITY,
        email: user.email ?? undefined,
      });
    });
    return () => {
      active = false;
    };
  }, []);

  return {
    name: saved || session?.name || NEUTRAL_IDENTITY,
    email: session?.email,
  };
}

/** Primeiro nome para a saudação ("Bem-vindo de volta, Gabriel."). */
export function firstName(identity: ProfileIdentity): string | null {
  if (identity.name === NEUTRAL_IDENTITY) return null;
  return identity.name.trim().split(/\s+/)[0] || null;
}
